/**
 * Image library domain types.
 *
 * Images and tags are global entities linked through a many-to-many
 * `ImageTagLink` (image_tags) relation. Field names follow the snake_case
 * convention of the existing backend so the mock can be swapped for real
 * API calls without remapping.
 */

export type LibraryImageSource = "upload" | "ai";

export interface LibraryTag {
  id: number;
  name: string;
  /** Number of images this tag is linked to. */
  image_count: number;
  created_at: string;
  updated_at: string;
}

export interface LibraryImage {
  id: number;
  name: string;
  url: string;
  thumbnail_url: string;
  source: LibraryImageSource;
  width: number | null;
  height: number | null;
  created_at: string;
  updated_at: string;
  tags: LibraryTag[];
}

/** Join table row (image_tags). */
export interface ImageTagLink {
  image_id: number;
  tag_id: number;
}

export type LibraryImageSort = "newest" | "oldest" | "name";

export interface LibraryImagesParams {
  /** Matches image name or tag name (case-insensitive). */
  search?: string;
  /** Only images linked to every one of these tags. */
  tag_ids?: number[];
  sort?: LibraryImageSort;
}

export interface LibraryImagesResponse {
  items: LibraryImage[];
  total: number;
}

export interface LibraryTagsParams {
  search?: string;
}

export interface CreateLibraryImagePayload {
  name: string;
  /** Url returned by the upload endpoint (or an AI generator). */
  url: string;
  source: LibraryImageSource;
  width?: number | null;
  height?: number | null;
  tag_ids: number[];
}

export interface UpdateLibraryImagePayload {
  name?: string;
  /** Replaces all linked tags when provided. */
  tag_ids?: number[];
}

export interface CreateLibraryTagPayload {
  name: string;
}

export interface UpdateLibraryTagPayload {
  name: string;
}

export interface UploadLibraryImageResponse {
  url: string;
  width: number | null;
  height: number | null;
}

/** ImageLibrary_ImageType_ValueEnum on the backend. */
export const IMAGE_LIBRARY_TYPE = {
  slider: 1,
  product: 2,
  topping: 3,
  category: 4,
  flyer: 5,
} as const;

export type ImageLibraryType =
  (typeof IMAGE_LIBRARY_TYPE)[keyof typeof IMAGE_LIBRARY_TYPE];

/** Item of GET /internal/secure/pos/v1/image-library. */
export interface ImageLibraryItem {
  item_id: number;
  name: string;
  description: string;
  image_type: ImageLibraryType;
  format: number;
  /** FranchiseInformation_KitchenType_Enum values. */
  kitchen_types: number[];
  is_ai_generated: boolean;
  cloudinary_id: string;
  /** Built by the backend from image_type and cloudinary_id. */
  url: string;
}

export interface ImageLibraryParams {
  image_type?: ImageLibraryType;
  /** One FranchiseInformation_KitchenType_Enum value. */
  kitchen_type?: number;
  menu_data_tag_id?: number;
}

export interface CreateFlyerExamplePayload {
  name: string;
  kitchen_types: number[];
  file: File;
}
